import i18next from 'i18next';
import moment, { Moment } from 'moment';
import {
    AdvanceSelectFilter,
    LogicOperator,
    Operator,
    SelectValue,
} from '@components-clean/core/study-data-filter/advanced-select/advanced-select.interface';
import {
    CategoryType,
    ChipData,
    FieldDefinitionFieldTypeNumeric,
    FilterCategory,
    FilterCategoryItems,
    FilterValue,
    PartialDateTimeIgnoreNumeric,
    RowDataSourceTableNumeric,
} from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import { CategoryConfig, FilterUICalculateFilters } from '@interfaces/central-monitoring';

const FLOAT_TOLERANCE = 0.0000000000000001;

export const MapConfigToFilterCategories = (siteAndSubject: Array<CategoryConfig>): Array<FilterCategory> => {
    const grouped = siteAndSubject.reduce<{
        siteItems: Array<FilterCategoryItems>;
        subjectItems: Array<FilterCategoryItems>;
        dataItems: Array<FilterCategoryItems>;
    }>(
        (prev, curr) => {
            switch (curr.filterContext) {
                case RowDataSourceTableNumeric.Site:
                    prev.siteItems.push({
                        id: curr.id,
                        label: curr.label,
                        isDefault: curr.isDefault,
                        index: curr.displayIndex,
                        dataType: curr.dataType,
                        context: curr.filterContext,
                        fieldKey: curr.fieldKey,
                    });
                    break;
                case RowDataSourceTableNumeric.Subject:
                    prev.subjectItems.push({
                        id: curr.id,
                        label: curr.label,
                        isDefault: curr.isDefault,
                        index: curr.displayIndex,
                        dataType: curr.dataType,
                        context: curr.filterContext,
                        fieldKey: curr.fieldKey,
                    });
                    break;
                case RowDataSourceTableNumeric.SiteIssue:
                case RowDataSourceTableNumeric.SiteProtocolDeviation:
                case RowDataSourceTableNumeric.SiteVisit:
                case RowDataSourceTableNumeric.SubjectAdverseEvent:
                case RowDataSourceTableNumeric.SubjectAssessment:
                case RowDataSourceTableNumeric.SubjectCrf:
                case RowDataSourceTableNumeric.SubjectInformedConsent:
                case RowDataSourceTableNumeric.SubjectIssue:
                case RowDataSourceTableNumeric.SubjectLab:
                case RowDataSourceTableNumeric.SubjectProtocolDeviation:
                case RowDataSourceTableNumeric.SubjectQuery:
                case RowDataSourceTableNumeric.SubjectVisit:
                case RowDataSourceTableNumeric.SubjectConcomitantMedication:
                case RowDataSourceTableNumeric.SubjectMedicalHistory:
                    prev.dataItems.push({
                        id: curr.id,
                        label: curr.label,
                        isDefault: curr.isDefault,
                        index: curr.displayIndex,
                        dataType: curr.dataType,
                        context: curr.filterContext,
                        fieldKey: curr.fieldKey,
                    });
                    break;
                case RowDataSourceTableNumeric.Study:
                default:
                    break;
            }

            return prev;
        },
        {
            siteItems: [],
            subjectItems: [],
            dataItems: [],
        },
    );

    const cats: Array<FilterCategory> = [];
    if (grouped.siteItems.length > 0)
        cats.push({
            id: CategoryType.Site,
            items: grouped.siteItems.sort((a, b) => a.index - b.index),
        });

    if (grouped.subjectItems.length > 0)
        cats.push({
            id: CategoryType.Subject,
            items: grouped.subjectItems.sort((a, b) => a.index - b.index),
        });

    if (grouped.dataItems.length > 0)
        cats.push({
            id: CategoryType.Data,
            items: grouped.dataItems.sort((a, b) => a.index - b.index),
        });

    return cats;
};

const mapContextToCategoryType = (context: RowDataSourceTableNumeric): CategoryType | null => {
    switch (context) {
        case RowDataSourceTableNumeric.Site:
            return CategoryType.Site;
        case RowDataSourceTableNumeric.Subject:
            return CategoryType.Subject;
        case RowDataSourceTableNumeric.SiteIssue:
        case RowDataSourceTableNumeric.SiteProtocolDeviation:
        case RowDataSourceTableNumeric.SiteVisit:
        case RowDataSourceTableNumeric.SubjectAdverseEvent:
        case RowDataSourceTableNumeric.SubjectAssessment:
        case RowDataSourceTableNumeric.SubjectCrf:
        case RowDataSourceTableNumeric.SubjectInformedConsent:
        case RowDataSourceTableNumeric.SubjectIssue:
        case RowDataSourceTableNumeric.SubjectLab:
        case RowDataSourceTableNumeric.SubjectProtocolDeviation:
        case RowDataSourceTableNumeric.SubjectQuery:
        case RowDataSourceTableNumeric.SubjectVisit:
        case RowDataSourceTableNumeric.SubjectConcomitantMedication:
        case RowDataSourceTableNumeric.SubjectMedicalHistory:
            return CategoryType.Data;
        case RowDataSourceTableNumeric.Study:
        default:
            return null;
    }
};

export const getExpansionPanelTitle = (type: CategoryType) => {
    switch (type) {
        case CategoryType.Site:
            return i18next.t('Site');
        case CategoryType.Subject:
            return i18next.t('subject');
        case CategoryType.Data:
            return i18next.t('data');
        default:
            break;
    }
};

export const MapCalculateFiltersToChips = (
    { filteredFields }: FilterUICalculateFilters,
    categories: Array<FilterCategory>,
) => {
    if (!filteredFields?.length) return [];

    return filteredFields
        .flatMap<ChipData>((x) => {
            const catType = mapContextToCategoryType(x.context as RowDataSourceTableNumeric);
            const parentCat = categories.find((c) => c.id === catType);
            const category = parentCat?.items.find((c) => c.fieldKey === x.fieldKey);

            if (catType == null || !parentCat || !category) {
                window.console.error('Unsupported filter UI category type');
                return [];
            }

            return [
                {
                    parentCategory: parentCat.id,
                    parentName: getExpansionPanelTitle(parentCat.id) ?? '',
                    category: category.label,
                    categoryId: category.id,
                    index: category.index,
                },
            ];
        })
        .sort((a, b) => {
            if (a.parentCategory === b.parentCategory) return a.index - b.index;
            return a.parentCategory - b.parentCategory;
        });
};

const ItemMatchesNullFilter = (op: Operator.Null | Operator.NotNull, value: FilterValue) => {
    if (op === Operator.Null) return value.value == null;

    return value.value !== null;
};

const BoolItemMatchesFilter = (op: Operator, filter: boolean, value: FilterValue): boolean => {
    if (value.value != null && typeof value.value !== 'boolean') return false;

    const primitive = value.value as boolean | null;

    switch (op) {
        case Operator.Equals:
            return primitive === filter;
        case Operator.NotEqual:
            return primitive !== filter;
        default:
            return false; // Shouldn't ever get here
    }
};

const DateTimeItemMatchesFilter = (op: Operator, filter: Moment, value: FilterValue): boolean => {
    if (value.value != null && typeof value.value !== 'string') return false; // Expect ISO string

    const isNull = value.value == null;

    if (isNull) {
        switch (op) {
            case Operator.Equals:
            case Operator.GreaterThan:
            case Operator.GreaterThanOrEqualTo:
                return false;
            case Operator.NotEqual:
            case Operator.LessThan:
            case Operator.LessThanOrEqualTo:
                return true;
            default:
                return false; // Shouldn't ever get here
        }
    }

    const primitive = moment(value.value);
    if (!primitive.isValid() && !isNull) return false;

    let dateUnit: moment.unitOfTime.StartOf | undefined;
    switch (value.dateTimeIgnore) {
        case PartialDateTimeIgnoreNumeric.IgnoreDay:
            dateUnit = 'month';
            break;
        case PartialDateTimeIgnoreNumeric.IgnoreTime:
            dateUnit = 'day';
            break;
        default:
            dateUnit = undefined;
            break;
    }

    switch (op) {
        case Operator.Equals:
            return primitive.isSame(filter, dateUnit);
        case Operator.NotEqual:
            return !primitive.isSame(filter, dateUnit);
        case Operator.GreaterThan:
            return primitive.isAfter(filter, dateUnit);
        case Operator.GreaterThanOrEqualTo:
            return primitive.isSameOrAfter(filter, dateUnit);
        case Operator.LessThan:
            return primitive.isBefore(filter, dateUnit);
        case Operator.LessThanOrEqualTo:
            return primitive.isSameOrBefore(filter, dateUnit);
        default:
            return false; // Shouldn't ever get here
    }
};

const IntegerItemMatchesFilter = (op: Operator, filter: number, value: FilterValue): boolean => {
    if (value.value != null && Number.isNaN(value.value as number)) return false;

    const primitive = value.value as number | null;
    const isNull = primitive == null;

    switch (op) {
        case Operator.Equals:
            return primitive === filter;
        case Operator.NotEqual:
            return primitive !== filter;
        case Operator.GreaterThan:
            return !isNull && primitive > filter;
        case Operator.GreaterThanOrEqualTo:
            return !isNull && primitive >= filter;
        case Operator.LessThan:
            return isNull || primitive < filter;
        case Operator.LessThanOrEqualTo:
            return isNull || primitive <= filter;
        default:
            return false; // Shouldn't ever get here
    }
};

const FloatItemMatchesFilter = (op: Operator, filter: number, value: FilterValue): boolean => {
    if (value.value != null && Number.isNaN(value.value as number)) return false;

    const primitive = value.value as number | null;
    const isNull = primitive == null;

    switch (op) {
        case Operator.Equals:
            return !isNull && Math.abs(primitive - filter) < FLOAT_TOLERANCE;
        case Operator.NotEqual:
            return isNull || Math.abs(primitive - filter) > FLOAT_TOLERANCE;
        case Operator.GreaterThan:
            return !isNull && primitive > filter;
        case Operator.GreaterThanOrEqualTo:
            return !isNull && (primitive > filter || Math.abs(primitive - filter) < FLOAT_TOLERANCE);
        case Operator.LessThan:
            return isNull || primitive < filter;
        case Operator.LessThanOrEqualTo:
            return isNull || primitive < filter || Math.abs(primitive - filter) < FLOAT_TOLERANCE;
        default:
            return false; // Shouldn't ever get here
    }
};

const StringItemMatchesFilter = (op: Operator, filter: string, value: FilterValue): boolean => {
    if (value.value != null && typeof value.value !== 'string') return false;

    const primitive = value.value?.toLocaleLowerCase();
    const comparisonValue = filter.toLocaleLowerCase();
    const isNull = primitive == null;

    switch (op) {
        case Operator.Equals:
            return primitive === comparisonValue;
        case Operator.NotEqual:
            return primitive !== comparisonValue;
        case Operator.Contains:
            return !isNull && primitive.indexOf(comparisonValue) > -1;
        case Operator.NotContains:
            return isNull || primitive.indexOf(comparisonValue) < 0;
        default:
            return false;
    }
};

const ProcessFilter = (filter: SelectValue, value: FilterValue) => {
    let matches = false;
    if (filter.operator == null) return true;

    if (filter.operator === Operator.Null || filter.operator === Operator.NotNull) {
        matches = ItemMatchesNullFilter(filter.operator, value);
    } else {
        // Expected to use the above Null/NotNull operators. Return false if value is null
        if (filter.value == null) return false;
        switch (filter.dataType) {
            case FieldDefinitionFieldTypeNumeric.Bool:
                matches = BoolItemMatchesFilter(filter.operator, filter.value, value);
                break;
            case FieldDefinitionFieldTypeNumeric.Date:
            case FieldDefinitionFieldTypeNumeric.PartialDate:
                matches = DateTimeItemMatchesFilter(filter.operator, filter.value as Moment, value);
                break;
            case FieldDefinitionFieldTypeNumeric.Int:
                matches = IntegerItemMatchesFilter(filter.operator, filter.value, value);

                break;
            case FieldDefinitionFieldTypeNumeric.Float:
                matches = FloatItemMatchesFilter(filter.operator, filter.value, value);

                break;
            case FieldDefinitionFieldTypeNumeric.Text:
                matches = StringItemMatchesFilter(filter.operator, filter.value, value);
                break;
            default:
                break;
        }
    }

    return matches;
};

export const ItemMatchesFilter = (value: FilterValue, filters: AdvanceSelectFilter): boolean => {
    if (filters.filters.length === 0) return true;

    if (filters.logic === LogicOperator.AND) {
        return filters.filters.every((filter) => {
            return ProcessFilter(filter, value);
        });
    }
    return filters.filters.some((filter) => {
        return ProcessFilter(filter, value);
    });
};
